<template>
  <div class="app-root w-full h-screen overflow-hidden" ref="appEl">
    <SvgStartpage />

    <router-link
      to="/"
      class="[ logo ] absolute left-0 top-0 z-30 select-none"
      :class="{ 'pointer-events-none text-gray-500 bg-gray-800': recording }">

      <header ref="headerEl" class="[ app-title ] absolute left-1 top-1 p-0 select-none z-20 bg-pink-800 hover:bg-pink-700"
        :class="{ '[ inactive ] filter grayscale brightness-150': recording }"
        @click="checkIfStartScreenShouldAnimate">

        <h1 ref="headerH1El" class="text-3xl text-blue-100 flex items-baseline">
          <strong>Geo</strong><span class="[ extralight ] font-extralight">Mover</span>
          <small ref="headerH1SmallEl" class="whitespace-nowrap block ml-3 text-xs font-medium tracking-widest overflow-hidden">v{{ appVersion }}</small>
        </h1>

        <p ref="headerPEl" class="text-blue-200 tracking-wider uppercase text-xs font-bold overflow-hidden whitespace-nowrap">Privacy First Geolocation Tools</p>

      </header>
    </router-link>

    <Preloader
      v-if="loading"
      :ready="startupReady"
      @hide-start="onPreloaderHideStart"
      @hidden="onPreloaderHidden"
    />

    <div
      v-if="!loading && startupFatalError"
      class="[ startup-fatal ] absolute inset-0 z-40 flex items-center justify-center p-6"
    >
      <div class="w-full max-w-xl rounded-md border border-red-300/40 bg-red-900/40 p-5 text-red-100">
        <h2 class="text-lg font-bold uppercase tracking-wide">Startup Error</h2>
        <p class="mt-2 text-sm leading-relaxed">{{ startupFatalError }}</p>
        <button class="btn btn-next mt-4" @click="reloadApp">Reload App</button>
      </div>
    </div>

    <Sidebar
      v-if="showPersistentSidebar"
      :sidebarComponents="sidebarComponents"
      :useNavigationComponents="sidebarNavigationComponents"
    />

    <router-view v-slot="{ Component }" v-if="!loading && !startupFatalError">
      <transition :name="animationName" mode="out-in" appear>
        <component :is="Component" />
      </transition>
    </router-view>

    <div class="[ svg-wrapper ] absolute inset-0" :class="{ 'svg-bg-active': startpage && setupFinished }">
      <svg ref="svgBgEl" class="[ svg-bg ] absolute left-0 top-full right-0 w-full h-full z-0">
        <use xlink:href="#intersection"></use>
      </svg>
    </div>

    <TouchScreenProtection
      :touchScreenProtectionActive="touchScreenProtectionActive"
      @disableTouchScreenProtection="disableTouchScreenProtectionActive"
      v-if="touchScreenProtectionActive"
    />

    <PwaBanner />
    <GlobalDeleteConfirmModal />

  </div>
</template>

<script setup>
import { version } from '@/../package.json';
import GeoMoverDB from '@/db/geoMoverDB';
import { computed, defineAsyncComponent, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useSettingsStore, useNewActivityStore, useUiStore, useSettingsPresetsStore, useActivityTypesStore, usePwaStore } from '@/stores';
import {
  createDeactivateStartScreenTimeline,
  createReactivateStartScreenTimeline,
  createStartScreenIntroTimeline
} from '@/animations/startScreen';
import SvgStartpage from '@/components/svgs/SvgStartpage';
import Preloader from '@/components/ui/Preloader.vue';
import gsap from 'gsap';
import { appDebugLog } from '@/utils/appDebugLog';

const TouchScreenProtection = defineAsyncComponent(() => import('@/components/ui/TouchScreenProtection.vue'));
const PwaBanner = defineAsyncComponent(() => import('@/components/ui/PwaBanner.vue'));
const GlobalDeleteConfirmModal = defineAsyncComponent(() => import('@/components/modal/GlobalDeleteConfirmModal.vue'));
const Sidebar = defineAsyncComponent(() => import('@/views/navigation/Sidebar.vue'));

const settingsStore = useSettingsStore();
const settingsPresetsStore = useSettingsPresetsStore();
const activityTypesStore = useActivityTypesStore();
const newActivityStore = useNewActivityStore();
const uiStore = useUiStore();
const pwaStore = usePwaStore();
const route = useRoute();

const animationName = ref('fade');
const appVersion = version;
const loading = ref(false);
const dbInitialised = ref(false);
const startupFatalError = ref('');
const showSvgBg = ref(false);
const rootPage = ref(false);

const appEl = ref(null);
const headerEl = ref(null);
const headerH1El = ref(null);
const headerH1SmallEl = ref(null);
const headerPEl = ref(null);
const svgBgEl = ref(null);
let ctx = null;
let logoResizeObserver = null;
const DB_INIT_TIMEOUT_MS = 12000;

const getStartScreenEls = () => {
  const header = headerEl.value ?? document.querySelector('header.app-title');
  const headerH1 = headerH1El.value ?? document.querySelector('header.app-title h1');
  const headerH1Small = headerH1SmallEl.value ?? document.querySelector('header.app-title h1 small');
  const headerP = headerPEl.value ?? document.querySelector('header.app-title p');
  const svgBg = svgBgEl.value ?? document.querySelector('.svg-bg');
  return { header, headerH1, headerH1Small, headerP, svgBg };
};

const dbReady = computed(() => settingsStore.dbReady);
const recording = computed(() => newActivityStore.recording);
const startScreenAnimationActive = computed(() => uiStore.startScreenAnimationActive);

const setupFinished = computed({
  get: () => settingsStore.setupFinished,
  set: (bool) => settingsStore.setSetupFinished(bool)
});

const startupReady = computed(() => dbInitialised.value && dbReady.value);
const showPersistentSidebar = computed(() => !loading.value && !startupFatalError.value);

const sidebarComponents = [];
const defaultNavigationComponents = Object.freeze({
  activityTypes: false,
  backup: false,
  information: false,
  notes: true,
  poi: true,
  routePlanning: false,
  services: false,
  trackDisplay: false,
  upload: false
});

const sidebarNavigationComponents = computed(() => {
  const name = route.name;
  const path = typeof route.path === 'string' ? route.path : '';

  if (name === 'track-recording') {
    return { ...defaultNavigationComponents, activityTypes: true, services: true, trackDisplay: true };
  }

  if (path.startsWith('/track-studio')) {
    return { ...defaultNavigationComponents, services: true, trackDisplay: true };
  }

  if (name === 'map-hosts' || path.startsWith('/services')) {
    return { ...defaultNavigationComponents, services: true };
  }

  if (name === 'operations') {
    return { ...defaultNavigationComponents, backup: true };
  }

  if (name === 'about-geomover') {
    return { ...defaultNavigationComponents, information: true, backup: true, upload: true };
  }

  if (name === 'points-of-interest') {
    return { ...defaultNavigationComponents, poi: true };
  }

  if (name === 'route-planning') {
    return { ...defaultNavigationComponents, routePlanning: true };
  }

  if (name === 'upload') {
    return { ...defaultNavigationComponents, upload: true };
  }

  return { ...defaultNavigationComponents, services: true };
});

const startpage = computed(() => {
  if (route.name !== 'start' && route.name != null) return false;
  return true;
});

const touchScreenProtectionActive = computed({
  get: () => settingsStore.touchScreenProtectionActive,
  set: (bool) => settingsStore.setTouchScreenProtectionActive(bool)
});

const disableTouchScreenProtectionActive = () => {
  touchScreenProtectionActive.value = false;
};

const reactivateStartScreenAnimation = () => {
  ctx?.reactivateStartScreenAnimation?.();
};

const startScreenAnimation = () => {
  ctx?.startScreenAnimation?.();
};

const deactivateStartScreenAnimation = () => {
  ctx?.deactivateStartScreenAnimation?.();
};

const checkForStartPageAnimation = () => {
  if (startpage.value) {
    startScreenAnimation();
    // Keep store in sync - we're on start page, animation is active
    uiStore.setStartScreenAnimationActive(true);
  }
  else {
    deactivateStartScreenAnimation();
    // Keep store in sync - we're not on start page, animation is inactive
    // This ensures the watcher will fire when navigating TO start page
    uiStore.setStartScreenAnimationActive(false);
  }
};

const onPreloaderHideStart = () => {
  showSvgBg.value = true;
  setupFinished.value = true;
  checkForStartPageAnimation();
};

const onPreloaderHidden = () => {
  loading.value = false;
};

const checkIfStartScreenShouldAnimate = () => {
  if (rootPage.value !== true) reactivateStartScreenAnimation();
};

const reloadApp = () => {
  window.location.reload();
};

const updateLogoRightOffset = () => {
  const header = headerEl.value;
  if (!header) return;

  const rect = header.getBoundingClientRect();
  const rightOffset = Math.ceil(rect.right + 8);
  document.documentElement.style.setProperty('--logo-right-offset', `${rightOffset}px`);
};

watch(startScreenAnimationActive, (newValue) => {
  if (newValue === false) deactivateStartScreenAnimation();
  else if (newValue === true) reactivateStartScreenAnimation();
});

watch(() => route.fullPath, () => {
  // Route is changing
  appDebugLog.info('App', 'Route is changing');
  appDebugLog.info('App', '> recording =', recording.value);
  appDebugLog.info('App', '> route.name =', route.name);

  if (route.name === 'track-studio-display-single') {
    animationName.value = 'slide';
  }
});

watch(
  () => route.name,
  (name) => {
    if (name === 'start') {
      uiStore.setActiveMenu('');
      uiStore.toggleMainNavStatus(false);
    }
  },
  { immediate: true }
);

onMounted(async () => {
  loading.value = true;

  await nextTick();
  updateLogoRightOffset();
  window.addEventListener('resize', updateLogoRightOffset);

  if (typeof ResizeObserver !== 'undefined' && headerEl.value) {
    logoResizeObserver = new ResizeObserver(updateLogoRightOffset);
    logoResizeObserver.observe(headerEl.value);
  }

  if (document.fonts?.ready) {
    document.fonts.ready.then(() => {
      updateLogoRightOffset();
    });
  }

  // Initialize PWA (install prompt + update detection)
  pwaStore.init();

  ctx = gsap.context(() => {}, appEl.value);

  ctx.add('reactivateStartScreenAnimation', () => {
    const { header, headerH1, headerH1Small, headerP, svgBg } = getStartScreenEls();
    if (!header || !headerH1 || !headerH1Small || !headerP || !svgBg) return;

    createReactivateStartScreenTimeline(
      { header, headerH1, headerH1Small, headerP, svgBg },
      { onComplete: () => { rootPage.value = true; } }
    ).play();
  });

  ctx.add('startScreenAnimation', () => {
    const { header, svgBg } = getStartScreenEls();
    if (!header || !svgBg) return;

    createStartScreenIntroTimeline(
      { header, svgBg },
      { onStart: () => { rootPage.value = true; } }
    ).play();
  });

  ctx.add('deactivateStartScreenAnimation', () => {
    const { header, headerH1, headerH1Small, headerP, svgBg } = getStartScreenEls();
    if (!header || !headerH1 || !headerH1Small || !headerP || !svgBg) return;

    createDeactivateStartScreenTimeline(
      { header, headerH1, headerH1Small, headerP, svgBg },
      { onStart: () => { rootPage.value = false; } }
    ).play();
  });

  // Speech Recognition setup _> TODO: should this be done in VueX Store (settings/actions: init)?
  // "webkit" prefix still needed as with Chrome 90
  settingsStore.setSpeechRecognitionAvailable('webkitSpeechRecognition' in window || 'SpeechRecognition' in window);

  await nextTick();
  let dbInitTimeoutId = null;
  const dbInitPromise = GeoMoverDB.init();
  const dbInitTimeoutPromise = new Promise(resolve => {
    dbInitTimeoutId = window.setTimeout(() => resolve(false), DB_INIT_TIMEOUT_MS);
  });

  dbInitialised.value = await Promise.race([dbInitPromise, dbInitTimeoutPromise]);
  if (dbInitTimeoutId != null) {
    window.clearTimeout(dbInitTimeoutId);
  }
  settingsStore.setDbReady(dbInitialised.value);

  if (!dbInitialised.value) {
    startupFatalError.value = 'The local database could not be initialized. Please reload the app. If this persists, close other GeoMover tabs and reload again.';
    loading.value = false;
    appDebugLog.error('App', `Database initialization failed or timed out after ${DB_INIT_TIMEOUT_MS}ms. Showing startup error screen.`);
    return;
  }

  await settingsStore.initSettings();
  await settingsPresetsStore.initPresets();

  // Keep startup state consistent: if a preset is active, apply its settings
  // and sync the current Activity Type (if the preset is not universal).
  const activePreset = settingsPresetsStore.activePreset;
  if (activePreset) {
    settingsStore.applyPresetSettings(activePreset);

    const activityTypeId = activePreset.activityTypeId;
    if (activityTypeId != null) {
      try {
        if (!activityTypesStore.activityTypes?.length) {
          await activityTypesStore.reloadActivityTypes();
        }

        activityTypesStore.setCurrentActivityType(activityTypeId);
        const activityTypeObj = activityTypesStore.activityTypes.find(a => a.id === activityTypeId) || null;
        activityTypesStore.setCurrentActivityTypeObj(activityTypeObj);
      }
      catch (error) {
        appDebugLog.warn('[App] Could not sync activity type from active preset:', error);
      }
    }
  }
});

onBeforeUnmount(() => {
  ctx?.revert();
  ctx = null;
  pwaStore.cleanup();
  window.removeEventListener('resize', updateLogoRightOffset);

  if (logoResizeObserver) {
    logoResizeObserver.disconnect();
    logoResizeObserver = null;
  }
});
</script>
